/**
 * Partner display fonts for PDF export HTML.
 *
 * Absolute FE URLs fail in Puppeteer: @font-face is subject to CORS, and the
 * export host is a different origin than the ContinuityCoach frontend. Logos
 * work via <img> (no CORS); fonts do not. Inline woff2 as data URIs instead.
 */
import avivaCurveLight from '../assets/fonts/aviva-curve/AvivaCurve-Light.woff2';
import avivaCurveRegular from '../assets/fonts/aviva-curve/AvivaCurve-Regular.woff2';
import avivaCurveMedium from '../assets/fonts/aviva-curve/AvivaCurve-Medium.woff2';
import avivaCurveBold from '../assets/fonts/aviva-curve/AvivaCurve-Bold.woff2';
import { isPartner } from '../themes/partnerTheme';

const AVIVA_CURVE_FACES = [
  { src: avivaCurveLight, weight: 300 },
  { src: avivaCurveRegular, weight: 400 },
  { src: avivaCurveMedium, weight: 500 },
  { src: avivaCurveBold, weight: 700 },
];

let fontFaceHtmlPromise = null;

function toAbsoluteAssetUrl(assetPath) {
  if (!assetPath) return '';
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  if (typeof window === 'undefined' || !window.location?.origin) return assetPath;
  return new URL(assetPath, window.location.origin).href;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

async function toDataUri(assetPath) {
  const url = toAbsoluteAssetUrl(assetPath);
  if (!url) return '';

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch font ${url}: ${response.status}`);

  const base64 = arrayBufferToBase64(await response.arrayBuffer());
  return `data:font/woff2;base64,${base64}`;
}

function buildFontFaceCss(family, faces) {
  return faces
    .map(({ src, weight }) => {
      if (!src) return '';
      return `
@font-face {
  font-family: '${family}';
  src: url('${src}') format('woff2');
  font-weight: ${weight};
  font-style: normal;
  font-display: block;
}`;
    })
    .filter(Boolean)
    .join('\n');
}

async function buildAvivaFontFaceHtml() {
  const faces = await Promise.all(
    AVIVA_CURVE_FACES.map(async ({ src, weight }) => ({
      src: await toDataUri(src),
      weight,
    })),
  );

  const css = buildFontFaceCss('Aviva Curve', faces);
  if (!css) return '';
  return `<style>${css}</style>`;
}

/**
 * `<style>` block with partner @font-face rules (data URIs), or empty when not needed.
 * Cached after the first successful load.
 */
export default function getPartnerExportFontFaceHtml() {
  if (!isPartner('aviva')) return Promise.resolve('');

  if (!fontFaceHtmlPromise) {
    fontFaceHtmlPromise = buildAvivaFontFaceHtml().catch((err) => {
      fontFaceHtmlPromise = null;
      console.warn('Failed to inline partner fonts for PDF export', err);
      // Last resort: absolute URLs (may still fail CORS on the export host).
      const css = buildFontFaceCss(
        'Aviva Curve',
        AVIVA_CURVE_FACES.map(({ src, weight }) => ({ src: toAbsoluteAssetUrl(src), weight })),
      );
      return css ? `<style>${css}</style>` : '';
    });
  }

  return fontFaceHtmlPromise;
}
